// Frontend JS-error reporter (Batch 2 of the event-logging feature).
//
// Side-effecting module: importing it installs global `error` +
// `unhandledrejection` listeners that POST caught exceptions to /events, which
// the authenticated, CSRF-protected EventsController stores as source:"frontend"
// error events. There are no exports.
//
// Three hard guarantees mirror the server:
//   1. Fail silent — every path is wrapped in try/catch and the fetch ends in
//      `.catch(()=>{})`. Reporting must never surface its own failure to the user.
//   2. No self-loop — a module-level `reporting` flag short-circuits the handlers
//      while a report is in flight, so an error thrown by the reporting fetch can
//      never be reported (which would spiral).
//   3. Throttle/dedupe per page load — an in-memory recent-message set drops
//      duplicates within 60s and caps the rolling-minute volume. The server has
//      its own per-user cap; this just spares the network in a tight error loop.
//
// Listeners are registered synchronously at import time (NOT inside
// DOMContentLoaded) so they catch errors as early as the bundle allows. The
// blind spot: an error firing before the CSRF meta tag is in the DOM can't post
// a valid token (422) — accepted per the spec.

const DEDUPE_WINDOW_MS = 60_000
const RATE_CAP = 10 // max reports per rolling minute before we drop excess
const STACK_LIMIT = 2000

// { message, ts } entries seen this page load. Pruned to the rolling window.
const recent = []
let reporting = false // self-loop guard: true while a report fetch is in flight

function shouldDrop(message) {
  const now = Date.now()
  // Prune anything older than the dedupe window.
  while (recent.length && now - recent[0].ts > DEDUPE_WINDOW_MS) recent.shift()
  // Dedupe: same message already seen inside the window.
  if (recent.some((r) => r.message === message)) return true
  // Rate cap: too many distinct reports inside the window.
  if (recent.length >= RATE_CAP) return true
  recent.push({ message, ts: now })
  return false
}

function report(payload) {
  try {
    if (reporting) return
    const message = payload && payload.message
    if (!message || shouldDrop(message)) return

    reporting = true
    fetch('/events', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify(payload)
    })
      .catch(() => {})
      .finally(() => {
        reporting = false
      })
  } catch {
    // The reporter itself must never throw into the handler chain.
    reporting = false
  }
}

window.addEventListener('error', (e) => {
  try {
    if (reporting) return
    report({
      message: e.message || String(e.error),
      context: {
        url: e.filename,
        line: e.lineno,
        col: e.colno,
        stack: (e.error?.stack || '').slice(0, STACK_LIMIT)
      }
    })
  } catch {
    // swallow — never report an error raised while reporting
  }
})

window.addEventListener('unhandledrejection', (e) => {
  try {
    if (reporting) return
    const reason = e.reason
    report({
      message: String(reason?.message || reason),
      context: {
        url: location.href,
        stack: (reason?.stack || '').slice(0, STACK_LIMIT)
      }
    })
  } catch {
    // swallow — never report an error raised while reporting
  }
})
